import { useEffect, useState, useCallback } from 'react';
import { Button, Input, Spin, Tooltip } from 'antd';
import {
  CheckCircleOutlined, ClockCircleOutlined, DownloadOutlined,
  EnvironmentOutlined, SearchOutlined, ShopOutlined,
  PlusOutlined, WhatsAppOutlined, AppstoreOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../config';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  navy:       '#7a0000',   // primary — used everywhere
  navyLight:  '#243d60',   // hover / lighter variant
  gold:       '#C8960C',   // accent — only for key highlights
  goldLight:  '#FDF3DC',   // gold tint background
  red:        '#B91C1C',   // verified / error
  green:      '#15803D',   // verified badge
  greenBg:    '#F0FDF4',
  amber:      '#B45309',   // pending text
  amberBg:    '#FFFBEB',
  amberBorder:'#FCD34D',
  white:      '#FFFFFF',
  bg:         '#F4F6FA',   // page background
  border:     '#E2E8F0',
  text:       '#1E293B',
  textMid:    '#475569',
  textLight:  '#94A3B8',
};

// ── Sector color palette (muted, professional) ────────────────────────────────
const SECTOR_COLORS = [
  '#7a0000','#1d6fa4','#166534','#6b21a8','#c2410c',
  '#0e7490','#9f1239','#1e40af','#065f46','#7c3aed',
  '#b45309','#0369a1','#831843','#3730a3','#14532d',
];
const sectorColor = (id) => SECTOR_COLORS[(Number(id) || 0) % SECTOR_COLORS.length];

function useDebounce(value, delay) {
  const [deb, setDeb] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDeb(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return deb;
}

// ── Business Card ─────────────────────────────────────────────────────────────
function BusinessCard({ entry, accentColor }) {
  const isPending  = (entry.verification_status || 'Pending') !== 'Verified';
  const location   = [entry.current_city, entry.district, entry.state].filter(Boolean).join(', ');
  const initials   = (entry.name || '?').trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const bizPhotos  = entry.business_photo ? entry.business_photo.split(',').filter(Boolean) : [];
  const coverPhoto = bizPhotos[0] || null;

  return (
    <div
      style={{
        background: C.white,
        borderRadius: 14,
        overflow: 'hidden',
        boxShadow: '0 1px 6px rgba(0,0,0,0.07), 0 4px 16px rgba(0,0,0,0.05)',
        border: `1px solid ${isPending ? C.amberBorder : C.border}`,
        display: 'flex', flexDirection: 'column', height: '100%',
        transition: 'transform 0.18s ease, box-shadow 0.18s ease',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = '0 8px 28px rgba(0,0,0,0.12)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 1px 6px rgba(0,0,0,0.07), 0 4px 16px rgba(0,0,0,0.05)';
      }}
    >
      {/* Top accent line */}
      <div style={{ height: 4, background: isPending ? C.amberBorder : accentColor, flexShrink: 0 }} />

      {/* Cover photo */}
      <div style={{ position: 'relative', height: 112, flexShrink: 0 }}>
        {coverPhoto
          ? <img src={coverPhoto} alt="business"
              style={{ width: '100%', height: '100%', objectFit: 'cover',
                filter: isPending ? 'saturate(0.5) brightness(0.95)' : 'none' }} />
          : <div style={{
              width: '100%', height: '100%',
              background: isPending
                ? 'linear-gradient(135deg, #fef3c7, #fde68a)'
                : `linear-gradient(135deg, ${accentColor}22, ${accentColor}44)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <ShopOutlined style={{ fontSize: 38, color: isPending ? C.amberBorder : `${accentColor}88` }} />
            </div>
        }

        {/* Gradient overlay (only when photo exists) */}
        {coverPhoto && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.38) 100%)',
          }} />
        )}

        {/* Pending ribbon */}
        {isPending && (
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0,
            background: 'rgba(180,83,9,0.92)',
            color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
            textAlign: 'center', padding: '3px 0',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
          }}>
            <ClockCircleOutlined style={{ fontSize: 10 }} />
            सत्यापन प्रतीक्षारत — Verification Pending
          </div>
        )}

        {/* Verified badge */}
        {!isPending && (
          <div style={{
            position: 'absolute', top: 8, right: 8,
            background: C.green, color: '#fff',
            borderRadius: 20, fontSize: 10, fontWeight: 700, padding: '3px 10px',
            display: 'flex', alignItems: 'center', gap: 4,
            boxShadow: '0 1px 6px rgba(0,0,0,0.2)',
          }}>
            <CheckCircleOutlined style={{ fontSize: 10 }} /> सत्यापित
          </div>
        )}

        {/* Extra photos */}
        {bizPhotos.length > 1 && (
          <div style={{
            position: 'absolute', bottom: 6, right: 8,
            background: 'rgba(0,0,0,0.55)', color: '#fff',
            borderRadius: 8, fontSize: 9, fontWeight: 600, padding: '2px 7px',
          }}>
            +{bizPhotos.length - 1} more
          </div>
        )}

        {/* Profile photo overlapping */}
        <div style={{ position: 'absolute', bottom: -24, left: 14 }}>
          {entry.profile_photo
            ? <img src={entry.profile_photo} alt={entry.name}
                style={{
                  width: 52, height: 52, borderRadius: '50%', objectFit: 'cover',
                  border: '3px solid #fff', boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                  filter: isPending ? 'saturate(0.6)' : 'none',
                }} />
            : <div style={{
                width: 52, height: 52, borderRadius: '50%',
                background: isPending ? C.amberBorder : accentColor,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 17, fontWeight: 800, color: '#fff',
                border: '3px solid #fff', boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
              }}>
                {initials}
              </div>
          }
        </div>
      </div>

      {/* Card body */}
      <div style={{ padding: '30px 14px 14px', flex: 1, display: 'flex', flexDirection: 'column' }}>

        {/* Name */}
        <div style={{
          fontSize: 15, fontWeight: 700, color: C.text,
          lineHeight: 1.3, marginBottom: 6,
        }}>
          {entry.name}
        </div>

        {/* Sector + Sub-sector tags */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 12 }}>
          {entry.sector_name && (
            <span style={{
              fontSize: 10.5, fontWeight: 700, borderRadius: 20, padding: '2px 10px',
              background: isPending ? C.amberBg : `${accentColor}15`,
              color: isPending ? C.amber : accentColor,
              border: `1px solid ${isPending ? C.amberBorder : accentColor + '40'}`,
            }}>
              {entry.sector_name}
            </span>
          )}
          {entry.sub_sector_name && (
            <span style={{
              fontSize: 10.5, fontWeight: 500, borderRadius: 20, padding: '2px 10px',
              background: '#EEF2FF', color: '#3730a3',
              border: '1px solid #C7D2FE',
            }}>
              {entry.sub_sector_name}
            </span>
          )}
        </div>

        {/* Details */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 14 }}>
          {entry.firm_name && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 7 }}>
              <ShopOutlined style={{ color: isPending ? C.amber : accentColor, fontSize: 12, marginTop: 2, flexShrink: 0 }} />
              <span style={{ fontSize: 12.5, color: C.text, fontWeight: 600, lineHeight: 1.4 }}>
                {entry.firm_name}
                {entry.establishment_year && (
                  <span style={{ fontWeight: 400, color: C.textLight, fontSize: 11.5 }}> · Est. {entry.establishment_year}</span>
                )}
              </span>
            </div>
          )}
          {location && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 7 }}>
              <EnvironmentOutlined style={{ color: '#DC2626', fontSize: 12, marginTop: 2, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: C.textMid, lineHeight: 1.4 }}>{location}</span>
            </div>
          )}
          {entry.business_about && (
            <p style={{
              fontSize: 11.5, color: C.textLight, lineHeight: 1.6,
              margin: '2px 0 0',
              display: '-webkit-box', WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              {entry.business_about}
            </p>
          )}
        </div>

        {/* Download button */}
        <Tooltip title="Business Card download — Coming Soon!">
          <Button disabled icon={<DownloadOutlined />} block
            style={{
              borderRadius: 8, height: 34, fontSize: 12, fontWeight: 600,
              color: C.textLight, borderColor: C.border, cursor: 'not-allowed',
            }}>
            Download Card
          </Button>
        </Tooltip>

        {/* Pending motivation */}
        {isPending && (
          <div style={{
            marginTop: 9, fontSize: 11, color: C.amber, textAlign: 'center',
            background: C.amberBg, border: `1px solid ${C.amberBorder}`,
            borderRadius: 7, padding: '5px 10px', lineHeight: 1.5,
          }}>
            सत्यापन कराएं — प्रोफाइल हाइलाइट होगी
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function PublicBusinessDirectory() {
  const [entries,     setEntries]     = useState([]);
  const [sectors,     setSectors]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [sector,      setSector]      = useState(null);
  const [subSector,   setSubSector]   = useState(null);
  const [page,        setPage]        = useState(1);
  const [total,       setTotal]       = useState(0);
  const [hasMore,     setHasMore]     = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const LIMIT = 18;

  const debouncedSearch = useDebounce(search, 400);

  const fetchSectors = useCallback(async () => {
    try {
      const res = await axios.get(`${baseUrl}/business-sector/public-list`);
      if (res.data.status) setSectors(res.data.result || []);
    } catch {}
  }, []);

  const fetchEntries = useCallback(async (pg = 1, append = false) => {
    if (pg === 1) setLoading(true); else setLoadingMore(true);
    try {
      const params = { page: pg, limit: LIMIT };
      if (debouncedSearch.trim()) params.search        = debouncedSearch.trim().slice(0, 100);
      if (sector)                  params.sector_id     = sector;
      if (subSector)               params.sub_sector_id = subSector;
      const res = await axios.get(`${baseUrl}/business-directory/public-list`, { params });
      if (res.data.status) {
        const list = res.data.result || [];
        setEntries(prev => append ? [...prev, ...list] : list);
        setTotal(res.data.total || list.length);
        setHasMore(pg * LIMIT < (res.data.total || 0));
        setPage(pg);
      }
    } catch {}
    finally { setLoading(false); setLoadingMore(false); }
  }, [debouncedSearch, sector, subSector]);

  useEffect(() => {
    if (!localStorage.getItem('memberToken')) {
      window.location.replace('/user-portal-login');
    }
  }, []);

  useEffect(() => { fetchSectors(); }, [fetchSectors]);
  useEffect(() => { fetchEntries(1, false); }, [fetchEntries]);

  const selectSector = (id) => { setSector(id); setSubSector(null); };

  const activeSectorData = sectors.find(s => String(s.id) === String(sector));
  const subSectors       = activeSectorData?.sub_sectors || [];
  const verifiedCount    = entries.filter(e => e.verification_status === 'Verified').length;
  const formUrl          = `${window.location.origin}/business-directory-form`;

  const shareWhatsApp = () => {
    const msg = `🙏 *ABRSVS व्यवसाय निर्देशिका*\n\nसमाज के ${total}+ व्यवसायों की सूची:\n${window.location.href}\n\nअपना व्यवसाय जोड़ें:\n${formUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'Segoe UI', 'Noto Sans Devanagari', sans-serif" }}>

      {/* ══ HERO ════════════════════════════════════════════════════════════ */}
      <div style={{
        background: 'linear-gradient(160deg, #7a0000 0%, #b71c1c 55%, #7a0000 100%)',
        boxShadow: '0 4px 20px rgba(120,0,0,0.35)',
        textAlign: 'center',
        padding: '14px 20px 0',
      }}>
        {/* Blessing */}
        <div style={{ fontSize: 11, color: '#f5d9a0', letterSpacing: 0.5, marginBottom: 14 }}>
          ।। ॐ ब्रह्मणे नमः ।। &nbsp; जय गुरु महाराज री सा 🙏
        </div>

        {/* Kheteshar image — centred */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
          <img
            src="/Shri_Kheteshwar.jpg"
            alt="श्री खेतेश्वर महाराज"
            style={{
              width: 90, height: 90,
              borderRadius: '50%',
              objectFit: 'cover', objectPosition: 'top center',
              border: '3px solid #FFD700',
              boxShadow: '0 0 0 4px rgba(255,215,0,0.2), 0 4px 16px rgba(0,0,0,0.35)',
            }}
          />
        </div>

        {/* Org name */}
        <div style={{
          fontSize: 'clamp(15px, 3.8vw, 22px)',
          fontWeight: 800, color: '#FFD700',
          lineHeight: 1.3, fontFamily: 'serif',
          marginBottom: 4,
        }}>
          अखिल भारतीय राजपुरोहित समाज विकास संस्थान
        </div>
        <div style={{ fontSize: 12, color: '#f5c842', marginBottom: 10, fontWeight: 500 }}>
          श्री खेतेश्वर ब्रह्मधाम तीर्थ, आसोतरा
        </div>

        {/* Divider */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 8, marginBottom: 10, maxWidth: 260, margin: '0 auto 10px',
        }}>
          <div style={{ flex: 1, height: 1, background: 'linear-gradient(to right, transparent, rgba(255,215,0,0.5))' }} />
          <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#FFD700' }} />
          <div style={{ flex: 1, height: 1, background: 'linear-gradient(to left, transparent, rgba(255,215,0,0.5))' }} />
        </div>

        {/* Page title */}
        <div style={{
          fontSize: 'clamp(14px, 3vw, 18px)',
          fontWeight: 700, color: '#fff', letterSpacing: 0.5, marginBottom: 14,
        }}>
          व्यवसाय निर्देशिका &nbsp;·&nbsp; Business Directory
        </div>

        {/* Stats */}
        <div style={{
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          gap: 0, marginBottom: 18,
        }}>
          {[
            { val: total,          label: 'व्यवसाय'  },
            { val: verifiedCount,  label: 'सत्यापित' },
            { val: sectors.length, label: 'क्षेत्र'  },
          ].map(({ val, label }, i) => (
            <>
              {i > 0 && (
                <div key={`div-${i}`} style={{
                  width: 1, height: 36,
                  background: 'rgba(255,215,0,0.25)',
                  margin: '0 24px',
                  flexShrink: 0,
                }} />
              )}
              <div key={label} style={{ textAlign: 'center', minWidth: 56 }}>
                <div style={{ fontSize: 24, fontWeight: 900, color: '#FFD700', lineHeight: 1 }}>{val ?? '—'}</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.65)', marginTop: 4 }}>{label}</div>
              </div>
            </>
          ))}
        </div>

        {/* Search strip */}
        <div style={{
          background: 'rgba(0,0,0,0.22)',
          borderTop: '1px solid rgba(255,215,0,0.18)',
          padding: '12px 16px 14px',
        }}>
          <div style={{ maxWidth: 600, margin: '0 auto', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Input
              prefix={<SearchOutlined style={{ color: C.textLight }} />}
              placeholder="नाम, फर्म, शहर, जिला से खोजें..."
              value={search}
              onChange={e => setSearch(e.target.value.slice(0, 100))}
              allowClear
              style={{
                flex: 1, minWidth: 200, height: 42,
                borderRadius: 8, fontSize: 14, border: 'none',
              }}
            />
            <Button
              href={formUrl}
              icon={<PlusOutlined />}
              style={{
                height: 42, borderRadius: 8, fontWeight: 700, fontSize: 13,
                background: '#FFD700', borderColor: '#FFD700', color: '#7a0000',
                whiteSpace: 'nowrap',
              }}
            >
              व्यवसाय जोड़ें
            </Button>
          </div>
        </div>
      </div>

      {/* ══ FILTER BAR ════════════════════════════════════════════════════════ */}
      <div style={{
        background: C.white,
        borderBottom: `1px solid ${C.border}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        {/* Sector chips */}
        <div style={{
          padding: '10px 16px 0',
          overflowX: 'auto', display: 'flex', gap: 8,
          scrollbarWidth: 'none',
        }}>
          <FilterChip
            active={sector === null}
            color={C.navy}
            onClick={() => selectSector(null)}
          >
            <AppstoreOutlined style={{ fontSize: 11, marginRight: 4 }} />
            सभी क्षेत्र
          </FilterChip>
          {sectors.map(s => (
            <FilterChip
              key={s.id}
              active={String(sector) === String(s.id)}
              color={sectorColor(s.id)}
              onClick={() => selectSector(String(s.id) === String(sector) ? null : String(s.id))}
            >
              {s.name}
            </FilterChip>
          ))}
        </div>

        {/* Sub-sector chips */}
        {subSectors.length > 0 ? (
          <div style={{
            padding: '7px 16px 10px',
            overflowX: 'auto', display: 'flex', gap: 7,
            scrollbarWidth: 'none',
            borderTop: `1px solid ${C.border}`,
          }}>
            <FilterChip
              active={subSector === null}
              color="#3730a3"
              sub
              onClick={() => setSubSector(null)}
            >
              सभी उप-क्षेत्र
            </FilterChip>
            {subSectors.map(ss => (
              <FilterChip
                key={ss.id}
                active={String(subSector) === String(ss.id)}
                color="#3730a3"
                sub
                onClick={() => setSubSector(String(ss.id) === String(subSector) ? null : String(ss.id))}
              >
                {ss.name}
              </FilterChip>
            ))}
          </div>
        ) : (
          <div style={{ paddingBottom: 10 }} />
        )}
      </div>

      {/* ══ CARDS ═════════════════════════════════════════════════════════════ */}
      <div style={{ maxWidth: 1220, margin: '0 auto', padding: '24px 16px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '90px 0' }}>
            <Spin size="large" />
            <div style={{ color: C.textLight, marginTop: 14, fontSize: 14 }}>लोड हो रहा है...</div>
          </div>
        ) : entries.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '90px 20px' }}>
            <div style={{ fontSize: 48, marginBottom: 14 }}>🔍</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: C.text, marginBottom: 6 }}>
              कोई व्यवसाय नहीं मिला
            </div>
            <div style={{ fontSize: 13, color: C.textLight }}>
              खोज बदलें या सभी क्षेत्र देखें
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12.5, color: C.textLight, marginBottom: 18 }}>
              <span style={{ fontWeight: 700, color: C.navy }}>{total}</span> व्यवसाय मिले
              {(search || sector || subSector) && (
                <span style={{ color: C.gold, fontWeight: 600 }}> — फ़िल्टर लागू</span>
              )}
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(272px, 1fr))',
              gap: 20,
            }}>
              {entries.map(entry => (
                <BusinessCard key={entry.id} entry={entry} accentColor={sectorColor(entry.sector_id)} />
              ))}
            </div>

            {hasMore && (
              <div style={{ textAlign: 'center', marginTop: 36 }}>
                <Button
                  onClick={() => fetchEntries(page + 1, true)}
                  loading={loadingMore}
                  style={{
                    height: 44, borderRadius: 10, fontWeight: 700,
                    padding: '0 40px', fontSize: 14,
                    background: C.navy, borderColor: C.navy, color: '#fff',
                  }}
                >
                  और देखें — Load More
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ══ CTA BANNER ════════════════════════════════════════════════════════ */}
      <div style={{ maxWidth: 1188, margin: '8px auto 40px', padding: '0 16px' }}>
        <div style={{
          background: C.white,
          border: `1px solid ${C.border}`,
          borderRadius: 16,
          padding: '32px 28px',
          textAlign: 'center',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          position: 'relative', overflow: 'hidden',
        }}>
          {/* Left accent bar */}
          <div style={{
            position: 'absolute', left: 0, top: 0, bottom: 0, width: 5,
            background: C.navy,
            borderRadius: '16px 0 0 16px',
          }} />

          <div style={{ fontSize: 18, fontWeight: 800, color: C.text, marginBottom: 6 }}>
            क्या आपका व्यवसाय यहाँ नहीं दिखता?
          </div>
          <div style={{ fontSize: 13.5, color: C.textMid, marginBottom: 22 }}>
            अपनी जानकारी दर्ज करें और समाज के हज़ारों लोगों तक पहुँचें
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button href={formUrl} icon={<PlusOutlined />}
              style={{
                height: 44, borderRadius: 10, fontWeight: 700, fontSize: 14,
                background: C.navy, borderColor: C.navy, color: '#fff',
              }}>
              व्यवसाय जोड़ें
            </Button>
            <Button icon={<WhatsAppOutlined />} onClick={shareWhatsApp}
              style={{
                height: 44, borderRadius: 10, fontWeight: 700, fontSize: 14,
                background: '#25D366', borderColor: '#25D366', color: '#fff',
              }}>
              WhatsApp पर शेयर करें
            </Button>
          </div>
        </div>
      </div>

      {/* ══ FOOTER — warm cream, matches maroon header family ════════════════ */}
      <footer style={{
        background: '#FDF5E6',
        borderTop: '3px solid #7a0000',
      }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px 0' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 28, paddingBottom: 28,
          }}>

            {/* Brand */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <img
                src="/Shri_Kheteshwar.jpg"
                alt="श्री खेतेश्वर महाराज"
                style={{
                  width: 54, height: 54, borderRadius: '50%',
                  objectFit: 'cover', objectPosition: 'top center',
                  border: '2px solid #7a0000',
                  marginBottom: 10,
                }}
              />
              <div style={{ fontSize: 15, fontWeight: 800, color: '#7a0000', marginBottom: 4 }}>
                ABRSVS
              </div>
              <div style={{ fontSize: 12, lineHeight: 1.7, color: '#6b3a1f', maxWidth: 220 }}>
                अखिल भारतीय राजपुरोहित<br />
                समाज विकास संस्थान<br />
                <span style={{ fontSize: 11, color: '#999' }}>
                  श्री खेतेश्वर ब्रह्मधाम तीर्थ, आसोतरा 
                </span>
              </div>
            </div>

            {/* Quick links */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#7a0000', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
                Quick Links
              </div>
              {[
                { href: '/business-directory-form',   label: 'अपना व्यवसाय जोड़ें' },
                { href: '/business-directory-update', label: 'जानकारी अपडेट करें'  },
              ].map(({ href, label }) => (
                <a key={href} href={href}
                  style={{ display: 'block', fontSize: 13, color: '#6b3a1f', textDecoration: 'none', marginBottom: 9 }}
                  onMouseEnter={e => e.target.style.color = '#7a0000'}
                  onMouseLeave={e => e.target.style.color = '#6b3a1f'}>
                  → {label}
                </a>
              ))}
            </div>

            {/* Privacy */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#7a0000', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
                Privacy
              </div>
              <div style={{ fontSize: 12, lineHeight: 2, color: '#6b3a1f' }}>
                🔒 फोन नंबर सार्वजनिक नहीं<br />
                📧 ईमेल सुरक्षित रखी जाती है<br />
                ✅ केवल सत्यापित जानकारी दिखती है
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div style={{
            borderTop: '1px solid #F0D9A0',
            padding: '12px 0 16px',
            display: 'flex', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: 8, alignItems: 'center',
          }}>
            <span style={{ fontSize: 12, color: '#999' }}>
              © {new Date().getFullYear()} ABRSVS. सर्वाधिकार सुरक्षित।
            </span>
            <span style={{ fontSize: 12, color: '#bbb' }}>
              🔒 Phone numbers are never shared publicly
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ── Filter chip component ─────────────────────────────────────────────────────
function FilterChip({ active, color, sub, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center',
      padding: sub ? '4px 12px' : '5px 14px',
      borderRadius: 20,
      fontSize: sub ? 11.5 : 12.5,
      fontWeight: active ? 700 : 500,
      cursor: 'pointer',
      border: `1.5px solid ${active ? color : C.border}`,
      background: active ? color : (sub ? '#F8FAFF' : C.white),
      color: active ? '#fff' : (sub ? '#3730a3' : C.textMid),
      transition: 'all 0.15s',
      whiteSpace: 'nowrap',
      outline: 'none',
      flexShrink: 0,
    }}>
      {children}
    </button>
  );
}
